// Service d'appel API pour envoyer la candidature vers le backend.
// Utilisation: submitApplication({ backendUrl, offerId, offerTitle, form })
// Retourne une promesse qui résout en void ou rejette avec Error.
import { backendUrl as defaultBackend } from "../config_prod";
import { FormState } from "../type/form-step";
import { ATSAttribution } from "./analyticsService";

type SubmitParams = {
  backendUrl?: string;
  offerId: string;
  offerTitle?: string | null;
  form: FormState;
  verificationToken?: string | null;
  attribution?: ATSAttribution;
  visitorId?: string;
  sessionId?: string;
};

export async function inscriptionServices({
  backendUrl = defaultBackend,
  offerId,
  offerTitle,
  form,
  verificationToken,
  attribution,
  visitorId,
  sessionId,
}: SubmitParams): Promise<{
  submitted: boolean;
  verificationRequired: boolean;
  eventId?: string;
  expiresInMinutes?: number;
}> {
  const fd = new FormData();
  fd.set("opportunity_id", offerId);
  if (form.cvAiConsent && form.cvPrefillToken) fd.set("cv_prefill_token", form.cvPrefillToken);
  if (offerTitle) fd.set("offer_title", offerTitle);
  const fullName = form.name || `${form.firstName} ${form.lastName}`.trim();
  fd.set("name", fullName);
  fd.set("first_name", form.firstName);
  fd.set("last_name", form.lastName);
  fd.set("role", form.role);
  fd.set("email", form.email);
  fd.set("phone", form.phone);
  fd.set("address", form.address || "");
  fd.set("lang", form.lang);
  fd.set("school_level", form.schoolLevel || "");
  fd.set("languages_json", JSON.stringify(form.languages || []));
  fd.set("language_code", form.languageCode || "");
  fd.set("language_level", form.languageLevel || "");
  fd.set("experience_months", form.experienceMonths || "");
  fd.set("available_date", form.availableDate || "NOW");
  fd.set("mobile", String(form.mobile));
  fd.set("rayon_km", form.rayonKm || "");
  fd.set("city", form.city || "");
  fd.set("work_type", JSON.stringify(form.workType || []));
  fd.set("contract_type", JSON.stringify(form.contractType || []));
  fd.set("min_salary", form.minSalary || "");
  fd.set("max_salary", form.maxSalary || "");
  fd.set("currency", form.currency || "EUR");
  fd.set("salary_basis", form.salaryBasis || (form.contractType.includes("freelance") ? "daily" : "annual"));
  fd.set("daily_rate_min", form.dailyRateMin || "");
  fd.set("daily_rate_max", form.dailyRateMax || "");
  fd.set("source", attribution?.source || "");
  fd.set("utm_source", attribution?.utmSource || "");
  fd.set("utm_medium", attribution?.utmMedium || "");
  fd.set("utm_campaign", attribution?.utmCampaign || "");
  fd.set("visitor_id", visitorId || "");
  fd.set("session_id", sessionId || "");
  if (verificationToken) {
    fd.set("verification_token", verificationToken);
  }
  if (form.resume) {
    fd.set("resume", form.resume);
  }
  if (form.profileImage) {
    fd.set("profile_image", form.profileImage);
  }

  const res = await fetch(
    `${backendUrl}/kscan/v1/ats/post/candidate/inscription`,
    {
      method: "POST",
      body: fd,
    },
  );
  let data: any = {};
  try {
    data = await res.json();
  } catch {
    // pas de JSON
  }
  if (data.status === 202) {
    return {
      submitted: false,
      verificationRequired: true,
      eventId: data?.data?.event_id ? String(data.data.event_id) : undefined,
      expiresInMinutes: data?.data?.expires_in_minutes,
    };
  }
  if (!res.ok || data.status !== 200) {
    throw new Error(data?.message || `HTTP ${res.status}`);
  }
  return { submitted: true, verificationRequired: false };
}
